import { useRowSelect } from '@table-library/react-table-library/select';
import { Action, State } from '@table-library/react-table-library/types/common';
import { Button, Label, TableItem } from 'components/atoms';
import { FormInput } from 'components/molecules';
import PopupForm from 'components/molecules/popup/form/PopupForm';
import { Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { threadsItemCount } from 'constants/pageSizes';
import { useTranslation } from 'react-i18next';
import {
  PiChatCircleText,
  PiCheck,
  PiIdentificationBadge,
  PiPlus,
} from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { useGetThreads } from 'services/chat';
import { THREAD_STATUS } from 'services/chat/types';
import { useGetLabelLightList } from 'services/labels/light';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { formatMessageDate } from 'utils/global/date.utils';
import { getPlainText } from 'utils/global/plainText.utils';
import { object, string } from 'yup';

const Threads = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const threads = useGetThreads({ itemCount: threadsItemCount });
  const labels = useGetLabelLightList();

  const nodes =
    threads.data?.pages.flat().map(t => ({
      ...t,
      id: t.messageThreadId,
    })) ?? [];

  const onSelectChange = (action: Action, state: State) => {
    navigate(`${action?.payload.id}`, { state: 'client-threads' });
  };

  const select = useRowSelect(
    { nodes },
    {
      onChange: onSelectChange,
    },
  );

  return (
    <TopbarPage
      topbar={
        <TopBar
          size='compact'
          useHelmet={false}
          title={t('threads.list')}
          customBackItem={<div className='none' />}
          topbarContent={
            <PopupForm
              trigger={
                <Button onlyIcon variant={BUTTON_VARIANT.submit}>
                  <Button.Description description={t('button.add')} />

                  <Button.Icon icon={<PiPlus />} />
                </Button>
              }
              header={
                <>
                  <PiIdentificationBadge /> {t('input.label.label')}
                </>
              }
              onSubmit={(values, helpers) => {
                navigate(`/label/${values.labelId}/threads/create`, {
                  state: 'client-threads',
                });
                helpers.resetForm();
              }}
              initialValues={{ labelId: '' }}
              validationSchema={object().shape({
                labelId: string().required(t('formik-error.required')),
              })}
              submitButton={
                <Button type='submit' variant={BUTTON_VARIANT.submit}>
                  <Button.Text text={t('button.text')} />
                </Button>
              }>
              <FormInput.Select
                label={t('input.label.label')}
                name='labelId'
                options={labels.data?.map(l => ({
                  value: l.id,
                  label: l.labelName,
                }))}
              />
            </PopupForm>
          }
        />
      }>
      <Table
        id={'threads'}
        size='compact'
        select={select}
        data={{
          nodes: nodes.map(n => ({
            ...n,
            id: `/label/${n.labelId}/threads/${n.messageThreadId}`,
          })),
        }}
        columns={[
          {
            label: t('input.label.label'),
            renderCell: thread => (
              <TableItem>
                <TableItem.Text text={thread.labelName} />
              </TableItem>
            ),
            resize: true,
          },
          {
            label: t('input.label.subject'),
            renderCell: thread => (
              <TableItem>
                <TableItem.Text text={thread.subject} />
                <TableItem.Text
                  isSupplementary
                  text={`${formatMessageDate(thread.lastMessage.messageCreatedAt)} - ${getPlainText(thread.lastMessage.body)}`}
                />
              </TableItem>
            ),
            resize: true,
          },
          {
            label: '',
            renderCell: thread => (
              <TableItem justifyPosition='end'>
                <div className='flex gap-4'>
                  {thread.hasUnreadMessage && (
                    <Label color='blue'>
                      <PiChatCircleText />
                    </Label>
                  )}
                  {thread.messageThreadStatus.id === THREAD_STATUS.closed && (
                    <Label color='gray'>
                      <PiCheck />
                    </Label>
                  )}
                </div>
              </TableItem>
            ),
          },
        ]}
      />
    </TopbarPage>
  );
};

export default Threads;
